import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, UPDATE_CHECK_INTERVALS } from '@translate-ai/shared';
import { useClearHistory, useSettings, useUpdateSettings } from '@/api/hooks';
import { ErrorMessage } from '@/components/ErrorMessage';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';
import { SelectField } from '@/components/SelectField';
import { useSavedFlash } from '@/hooks/useSavedFlash';
import { useUILocale } from '@/hooks/useUILocale';
import type {
  UiLocale,
  UpdateCheckInterval,
  UserSettings,
} from '@translate-ai/shared';

export function Settings() {
  const { t, i18n } = useTranslation();
  const settingsQ = useSettings();
  const update = useUpdateSettings();
  const clear = useClearHistory();

  const [form, setForm] = useState<UserSettings | null>(null);
  const [savedFlash, triggerSavedFlash] = useSavedFlash();
  const [validationError, setValidationError] = useState<string | null>(null);
  const locale = useUILocale();

  useEffect(() => {
    if (settingsQ.data) setForm(settingsQ.data);
  }, [settingsQ.data]);

  if (!form) return <LoadingPlaceholder />;

  const toggleLanguage = (code: string) => {
    const next = form.activeLanguages.includes(code)
      ? form.activeLanguages.filter((c) => c !== code)
      : [...form.activeLanguages, code];
    setForm({ ...form, activeLanguages: next });
    if (next.length > 0) setValidationError(null);
  };

  const onSave = async () => {
    if (form.activeLanguages.length === 0) {
      setValidationError(t('settings.activeLanguagesEmpty'));
      return;
    }
    setValidationError(null);
    const saved = await update.mutateAsync(form);
    setForm(saved);
    if (saved.uiLocale !== i18n.resolvedLanguage) void i18n.changeLanguage(saved.uiLocale);
    triggerSavedFlash();
  };

  const onDeleteAll = () => {
    if (window.confirm(t('history.clearConfirm'))) clear.mutate();
  };

  return (
    <div className="space-y-6 py-2">
      <h1 className="text-xl font-semibold">{t('settings.title')}</h1>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-slate-700">{t('settings.general')}</h2>
        <label className="block">
          <span className="mb-1 block text-sm text-slate-600">{t('settings.uiLanguage')}</span>
          <SelectField<UiLocale>
            value={form.uiLocale}
            onChange={(uiLocale) => setForm({ ...form, uiLocale })}
            options={[
              { value: 'en', label: 'English' },
              { value: 'ru', label: 'Русский' },
            ]}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-slate-600">
            {t('settings.updateCheck')}
          </span>
          <SelectField<UpdateCheckInterval>
            value={form.updateCheckInterval}
            onChange={(updateCheckInterval) =>
              setForm({ ...form, updateCheckInterval })
            }
            options={UPDATE_CHECK_INTERVALS.map((opt) => ({
              value: opt,
              label: t(`settings.updateCheckOption.${opt}`),
            }))}
          />
        </label>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-slate-700">{t('settings.activeLanguages')}</h2>
        <p className="text-sm text-slate-500">{t('settings.activeLanguagesHint')}</p>
        <div className="grid grid-cols-2 gap-2">
          {SUPPORTED_LANGUAGES.map((l) => (
            <label key={l.code} className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.activeLanguages.includes(l.code)}
                onChange={() => toggleLanguage(l.code)}
              />
              <span>{locale === 'ru' ? l.nameRu : l.nameEn}</span>
            </label>
          ))}
        </div>
        <ErrorMessage message={validationError} />
        <button className="btn-primary w-full" onClick={onSave} disabled={update.isPending}>
          {savedFlash ? t('settings.saved') : t('settings.save')}
        </button>
      </section>

      <section className="space-y-2 border-t pt-4">
        <h2 className="text-sm font-medium text-rose-700">{t('settings.danger')}</h2>
        <button onClick={onDeleteAll} className="btn-secondary w-full text-rose-700">
          {t('settings.deleteAllHistory')}
        </button>
      </section>
    </div>
  );
}
